import React from 'react'
import { DatePicker } from 'antd'
import { InputNumber } from '@surecompanies/core_components'
import dayjs from 'dayjs'
import isNil from 'lodash/isNil'
import styles from './UploadReview.module.scss'
import { InvoiceTemplate } from '../../../api/dto/invoice-delivery.dto'
import {
  DATE_INPUT_FORMATS,
  DATE_INPUT_PLACEHOLDER,
  DUE_DATE_WIRE_FORMAT,
  formatAmountInput,
  parseAmountInput,
} from '../utils'
import { formatCurrency } from '../generation/utils'
import { ReviewRow } from './reviewRows'
import { isOe1TotalMismatched, RowEdits, sumOe1Amounts } from './reviewValues'
import {
  InputLine,
  LineLabel,
  LineValue,
  TextLine,
} from './UploadReview.styles'

export type OnEdit = (key: string, edits: RowEdits) => void

export const AmountInput: React.FC<{
  value: number | null
  label: string
  onChange: (value: number | null) => void
}> = ({ value, label, onChange }) => (
  <InputNumber
    value={value ?? undefined}
    onChange={(v) => onChange(typeof v === 'number' ? v : null)}
    placeholder="0.00"
    min={0}
    precision={2}
    step={0.01}
    prefix="$"
    formatter={formatAmountInput}
    parser={parseAmountInput}
    aria-label={label}
    className={styles.amountInput}
  />
)

export const DateInput: React.FC<{
  value: string | null
  label: string
  onChange: (value: string | null) => void
}> = ({ value, label, onChange }) => (
  <DatePicker
    value={isNil(value) ? null : dayjs(value)}
    onChange={(date) =>
      onChange(isNil(date) ? null : date.format(DUE_DATE_WIRE_FORMAT))
    }
    format={DATE_INPUT_FORMATS}
    placeholder={DATE_INPUT_PLACEHOLDER}
    aria-label={label}
    className={styles.dateInput}
  />
)

/*
 * OE1 rows add lines under the first: the balance and the OE total. Every
 * column draws the same lines so they stay level.
 */
export const renderTemplateLines = (row: ReviewRow) => {
  if (row.template === InvoiceTemplate.OE_FIRST) {
    return (
      <>
        <InputLine>
          <LineLabel>Second payment</LineLabel>
        </InputLine>
        <TextLine>Open Enrollment total</TextLine>
      </>
    )
  }
  return null
}

export const renderAmountLines = (row: ReviewRow, onEdit: OnEdit) => {
  const { values } = row
  if (row.template === InvoiceTemplate.OE_FIRST) {
    const sum = sumOe1Amounts(values)
    return (
      <>
        <InputLine>
          <AmountInput
            value={values.balanceDue}
            label={`Second payment amount for ${row.file.name}`}
            onChange={(balanceDue) => onEdit(row.key, { balanceDue })}
          />
        </InputLine>
        <TextLine $tone={isOe1TotalMismatched(values) ? 'warning' : undefined}>
          <LineValue>{isNil(sum) ? '—' : formatCurrency(sum)}</LineValue>
        </TextLine>
      </>
    )
  }
  return null
}

export const renderDueDateLines = (row: ReviewRow) => {
  if (row.template !== InvoiceTemplate.OE_FIRST) return null
  const { oeTotal } = row.values
  return (
    <>
      <InputLine />
      {isOe1TotalMismatched(row.values) && !isNil(oeTotal) ? (
        <TextLine $tone="warning" role="alert">
          Sheet total is {formatCurrency(oeTotal)}
        </TextLine>
      ) : null}
    </>
  )
}
